import { observer } from 'mobx-react';
import useAsyncEffect from 'use-async-effect';
import { SpcSteps } from './SpcModels';
import { useParams } from 'react-router-dom';
import SpcViewModel from './SpcViewModel';
import { Appl } from '../../../../core/Appl';
import { Button, InputOptionBox } from '../../../../core/components/Index';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../../../core/components/OptionSelectionAccordian';

const SpcServiceType = observer(() => {
  const vm = Appl.Services.get<SpcViewModel>('SpcViewModel');
  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(SpcSteps.ServiceType, memberId!);
  }, []);

  return (
    <>
     <div className="max-w-6xl">
      <h1 className="text-32px tracking-[-1%] text-[#363636] font-semibold mb-[32px]">Choose Your Service Type</h1>
      <div className="text-black text-15px tracking--1percent leading-26px space-y-4 w-full max-w-[801px]">
        <p>
          SCERS offers several ways for members to purchase additional service credit, depending on your work history and circumstances. Purchasing service credit may increase your retirement benefit or allow you to retire earlier.
        </p>
        <p>
          If you wish to pursue an official Request for Service Purchase Calculation (Form 6401), SCERS requires that you first generate a Service Purchase Calculator (SPC) Estimate using this tool. The estimate you generate here will be saved in your MySCERS account and must be attached to your Form 6401 submission.
        </p>
        <p> 
          More information about service purchases can be found in the <a href="https://www.scers.gov/post/service-purchase-policy" className="underline text-blue-600 hover:text-blue-800" target="_blank" rel="noopener noreferrer">SCERS' Service Purchase Policy</a>.
        </p>
      </div>
      <p className="text-15px font-bold text-black mt-[32px] mb-[8px]">Please review the options below carefully and select the type of service that applies to your situation.</p>
      
    <Accordion
      type="multiple"
      className="w-full max-w-[870px] w-full"
    >
      {/* 1 — Medical Leave of Absence (spc_type_id = 1) */}
      <AccordionItem value="item-1" withBorder={false}>

        <div className="flex items-center">
          {/* Checkbox */}
          <div className="flex-none pr-3">
            <InputOptionBox
              inputId="spcTypeId-1"
              label="Medical Leave of Absence"
              hideLabel
              value={1}
              disabled={vm.unsavedEstimate === false}
              className={
                !vm.unsavedEstimate
                  ? `
                    pointer-events-none
                    opacity-50
                    cursor-not-allowed  
                    [&_*]:pointer-events-none
                  `
                  : ""
              }
              checked={vm.model.spcTypeId?.toString() === "1"}
              onChange={(e) => {
                vm.onInputChange(
                  vm.model.spcTypeId?.toString() === "1"
                    ? { target: { value: null } }
                    : { target: { value: 1 } },
                  "spcTypeId",
                  vm.model
                );
              }}
            />
          </div>
            <div className="flex-1">
              <div className="flex items-center border-b border-[#DBDBDB]">

                {/* Title + underline */}
                <label className={vm.unsavedEstimate === false ? "flex-1 font-bold opacity-50" : "flex-1 font-bold"} htmlFor='spcTypeId-1'>
                  Medical Leave of Absence
                </label>

                {/* Accordion toggle */}
                <AccordionTrigger disabled={vm.unsavedEstimate === false} className={vm.unsavedEstimate === false ? "opacity-50 cursor-not-allowed flex-none px-[-16px] hover:bg-transparent" :"flex-none px-[-16px]"}>
                  <span className="sr-only ">Toggle</span>
                </AccordionTrigger>

              </div>
            </div>
          </div>

        <AccordionContent>
          <div className="text-[15px] leading-[175%] tracking-[-1.5%] text-black space-y-[20px] pl-6 w-full max-w-[855px] mt-[10px]">

            <p>
              If you took an unpaid leave of absence due to your own illness, you may purchase service credit for the period of leave.
            </p>

            <div>
              <p className="mb-[8px]">Restrictions:</p>
              <ul className="list-disc pl-[20px] space-y-[4px]">
                <li>The maximum service credit purchase for any continuous period of medical leave is one (1) year, which for calculation purposes equals 2,080 hours.</li>
                <li>You must return to active SCERS-covered employment after the leave.</li>
                <li>The purchase requires payment of missed contributions plus accrued interest for the period of leave.</li>
              </ul>
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>

      {/* 2 — Pre-Membership Employment (spc_type_id = 2) */}
      <AccordionItem value="item-2" withBorder={false}>
        <div className="flex items-center">

          <div className="flex-none pr-3">
            <InputOptionBox
              inputId="spcTypeId-2"
              label="Pre-Membership Employment"
              hideLabel
              value={2}
              disabled={vm.unsavedEstimate === false}
              className={
                !vm.unsavedEstimate
                  ? `
                    pointer-events-none
                    opacity-50
                    cursor-not-allowed  
                    [&_*]:pointer-events-none
                  `
                  : ""
              }
              checked={vm.model.spcTypeId?.toString() === "2"}
              onChange={(e) => {
                vm.onInputChange(
                  vm.model.spcTypeId?.toString() === "2"
                    ? { target: { value: null } }
                    : { target: { value: 2 } },
                  "spcTypeId",
                  vm.model
                );
              }}
            />
          </div>
          <div className="flex-1">
              <div className="flex items-center border-b border-[#DBDBDB]">
                <label className={vm.unsavedEstimate === false ? "flex-1 font-bold opacity-50" : "flex-1 font-bold"} htmlFor='spcTypeId-2'>
                  Pre-Membership Employment
                </label>
                <AccordionTrigger disabled={vm.unsavedEstimate === false} className={vm.unsavedEstimate === false ? "opacity-50 cursor-not-allowed flex-none px-[-16px] hover:bg-transparent" :"flex-none px-[-16px]"}>
                  <span className="sr-only">Toggle</span>
                </AccordionTrigger>

              </div>
            </div>
        </div>

        <AccordionContent>
          <div className="text-[15px] leading-[175%] tracking-[-1.5%] text-black space-y-[20px] pl-6 w-full max-w-[855px] mt-[10px]">

            <p>
              You may purchase service credit for employment with a SCERS-participating employer
              that occurred before you became a SCERS member. Eligible employment includes
              temporary, seasonal, or on-call positions that did not qualify for SCERS membership at the time.
            </p>

            <div>
              <p className="mb-[8px]">Restrictions:</p>
              <ul className="list-disc pl-[20px] space-y-[4px]">
                <li>Service performed as a contractor or through a temporary staffing agency is not eligible.</li>
                <li>SCERS may require verification of earnings and employment from the employer.</li>
              </ul>
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>

      {/* 3 — Redeposit (spc_type_id = 3) */}
      <AccordionItem value="item-3" withBorder={false}>
        <div className="flex items-center">

          <div className="flex-none pr-3">
            <InputOptionBox
              inputId="spcTypeId-3"
              label="Redeposit of Previously Withdrawn Contributions"
              hideLabel
              value={3}
              disabled={vm.unsavedEstimate === false}
              className={
                !vm.unsavedEstimate
                  ? `
                    pointer-events-none
                    opacity-50
                    cursor-not-allowed  
                    [&_*]:pointer-events-none
                  `
                  : ""
              }
              checked={vm.model.spcTypeId?.toString() === "3"}
              onChange={(e) => {
                vm.onInputChange(
                  vm.model.spcTypeId?.toString() === "3"
                    ? { target: { value: null } }
                    : { target: { value: 3 } },
                  "spcTypeId",
                  vm.model
                );
              }}
            />
          </div>

          <div className="flex-1">
              <div className="flex items-center border-b border-[#DBDBDB]">

                {/* Title + underline */}
                <label className={vm.unsavedEstimate === false ? "flex-1 font-bold opacity-50" : "flex-1 font-bold"} htmlFor='spcTypeId-3'>
                  Redeposit of Previously Withdrawn Contributions
                </label>

                {/* Accordion toggle */}
                <AccordionTrigger disabled={vm.unsavedEstimate === false} className={vm.unsavedEstimate === false ? "opacity-50 cursor-not-allowed flex-none px-[-16px] hover:bg-transparent" :"flex-none px-[-16px]"}>
                  <span className="sr-only">Toggle</span>
                </AccordionTrigger>

              </div>
            </div>
        </div>

        <AccordionContent>
          <div className="text-[15px] leading-[175%] tracking-[-1.5%] text-black space-y-[20px] pl-6 w-full max-w-[855px] mt-[10px]">

            <p>
              If you withdrew your SCERS contributions after terminating membership, you may redeposit the funds,
              with interest, to restore your service credit and potentially retain your original benefit tier.
            </p>

            <div>
              <p className="mb-[8px]">Restrictions:</p>
              <ul className="list-disc pl-[20px] space-y-[4px]">
                <li>The full amount of withdrawn contributions must be repaid, including interest. Partial redeposits are not allowed.</li>
                <li>Forgoing a redeposit means prior service credit will not count toward vesting or retirement eligibility.</li>
              </ul>
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>

      {/* 4 — Public Service (spc_type_id = 4) */}
      <AccordionItem value="item-4" withBorder={false}>
        <div className="flex items-center">

          <div className="flex-none pr-3">
            <InputOptionBox
              inputId="spcTypeId-4"
              label="Public Service"
              hideLabel
              value={4}
              disabled={vm.unsavedEstimate === false}
              className={
                !vm.unsavedEstimate
                  ? `
                    pointer-events-none
                    opacity-50
                    cursor-not-allowed  
                    [&_*]:pointer-events-none
                  `
                  : ""
              }
              checked={vm.model.spcTypeId?.toString() === "4"}
              onChange={(e) => {
                vm.onInputChange(
                  vm.model.spcTypeId?.toString() === "4"
                    ? { target: { value: null } }
                    : { target: { value: 4 } },
                  "spcTypeId",
                  vm.model
                );
              }}
            />
          </div>

          <div className="flex-1">
              <div className="flex items-center border-b border-[#DBDBDB]">

                {/* Title + underline */}
                <label className={vm.unsavedEstimate === false ? "flex-1 font-bold opacity-50" : "flex-1 font-bold"} htmlFor='spcTypeId-4'>
                  Public Service
                </label>

                {/* Accordion toggle */}
                <AccordionTrigger disabled={vm.unsavedEstimate === false} className={vm.unsavedEstimate === false ? "opacity-50 cursor-not-allowed flex-none px-[-16px] hover:bg-transparent" :"flex-none px-[-16px]"}>
                  <span className="sr-only">Toggle</span>
                </AccordionTrigger>

              </div>
            </div>
        </div>

        <AccordionContent>
          <div className="text-[15px] leading-[175%] tracking-[-1.5%] text-black space-y-[20px] pl-6 w-full w-full max-w-[855px] mt-[10px]">

            <p>
              You may purchase service credit for eligible public employment with entities such as the federal government,
              State of California, or other municipalities and districts within Sacramento County.
            </p>

            <div>
              <p className="mb-[8px]">Restrictions:</p>
              <ul className="list-disc pl-[20px] space-y-[4px]">
                <li>A maximum of four (4) years of public service credit may be purchased, which for calculation purposes equals 8,320 hours.</li>
                <li>Service covered by another retirement system cannot be purchased if the retirement contributions can be redeposited with full reciprocal rights.</li>
                <li>If you later become eligible for a pension from the public entity, your SCERS service credit and contributions for that period may be adjusted.</li>
              </ul>
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
      <div className="flex mt-6 flex-wrap gap-2 sm:flex-nowrap">
        <Button
          label="Continue"
          rounded
          onClick={async (e:any) => {e.preventDefault();await vm.onServiceTypeStepChangeAsync()}}
          disabled={vm.model.spcTypeId! < 1}
        />
        {vm.unsavedEstimate === false &&
        (
          <Button 
            title="Change Service Type" 
            className='w-[160px] h-[39px]' 
            severity='warning' 
            outlined rounded 
            onClick={async (e) => {
              e.stopPropagation(); 
              e.preventDefault();

              Appl.ConfirmBox.show(
                'Changing the service type will clear any details you have already entered. Do you want to continue?',
                'Change Service Type?',
                '',
                "Yes, I am sure",
                "Cancel",
                async () => await vm.resetSavedEstimateAsync(memberId!, true),
                undefined,
                true
              );
            }} 
          />
        )}
      </div>
    </div>
    </>
  );
});

export default SpcServiceType;
